/* eslint-disable import/prefer-default-export */
import { createSelector } from 'reselect';
import { emptyList, emptyObject } from '@constants/empty';
import { DPP_CARD_VISIBILITY_STATUS } from '@educator/Container/AutoDPP/constants';

export const educatorState = (state) => state;

export const getAllLanguages = ({ educator }) =>
  educator.allLanguages || emptyList;

export const getEducatorFeedbackForTemplate = createSelector(
  educatorState,
  (state) => state.educator.feedBackOptionsForATemplateCourse
);

export const getEducatorState = createSelector(
  educatorState,
  (state) => state.educator ?? {}
);

const getContentOperator = ({ accessControl }) =>
  accessControl?.isContentOperator || false;

export const getTemplateData = ({ templates }) =>
  templates.addEditTemplate ?? {};

export const courseTemplatetSelector = createSelector(
  getContentOperator,
  getTemplateData,
  (isContentOperator, templateData) => ({
    isContentOperator,
    templateData
  })
);

export const getBillingDetailsSelector = (state) => {
  return state.educatorOnboarding.billingDetails;
};

export const getIFSCDetailsForUser = (state) => {
  return state.educatorOnboarding.ifscDetails;
};
export const getOnboardingState = ({ educatorOnboarding }) =>
  educatorOnboarding.onboardingState;

export const getNameForBillingDetails = (state) => {
  return state.educatorOnboarding.billingDetails.name;
};

export const getFaqLevelsData = (state) => {
  const faqData = state.educator.faqLevels.filter((faq) =>
    faq.isVisibleForLearner ? faq : state.auth.me.isEducator && faq
  );
  return faqData;
};

export const getIntroLevelsData = ({ educator }) => educator.introlLevels;

export const getDppReminderStatus = ({ educator }) =>
  educator.dppReminderStatus;

export const getShowLevelsIntroCard = ({ educator }) =>
  educator.metaInfo.home_feed_blocks?.educator_levels_intro_card?.is_visible;

export const getShowClassPracticesTab = ({ educator }) =>
  educator.metaInfo.home_feed_blocks?.class_practice_tab?.is_visible;

export const getShowTeachersDayBanner = ({ educator }) =>
  educator.metaInfo?.home_feed_blocks?.teachers_day_unwrap?.is_visible;

export const getEducatorLevelInfo = ({ educator }) => educator.levelInfo;

export const getModalState = ({ educator }) => educator.modal || emptyObject;

export const getEducatorLevelPromotionMetadata = ({ educator }) =>
  educator?.levelInfo?.promotion_metadata;

export const getEducatorLevelStats = ({ educator }) => educator.levelStats;

export const getEducatorLevelWiseEducators = ({ educator }) =>
  educator.levelWiseEducators;

export const getGoalUidForStats = ({ educator }) => educator.goalUidStats;

export const getCommunityShareCard = ({ educator }) =>
  educator.communityShareData;

export const getCommunityDetails = ({ educator }) => educator.communityDetails;

export const getSchedulerAwarenessInfo = ({ educator }) =>
  educator.schedulerAwarenessInfo;

export const getBannnersInfo = ({ educator }) => educator.bannersInfo;

export const getIsDppIntroCardVisible = ({ educator }) =>
  educator.metaInfo.home_feed_blocks?.dpp_intro_card?.is_visible;

export const getIsDppIntroCardVideoURL = ({ educator }) =>
  educator.metaInfo.home_feed_blocks?.dpp_intro_card?.video_url;

export const getDppReminderVisibility = ({ educator }) =>
  educator?.dppReminderStatus?.is_visible ??
  DPP_CARD_VISIBILITY_STATUS.NOT_KNOWN;

export const getDppSummaryBannerVisibility = ({ educator }) =>
  educator?.metaInfo?.home_feed_blocks?.dpp_summary_banner?.is_visible ??
  DPP_CARD_VISIBILITY_STATUS.NOT_KNOWN;

export const getDppSummaryBannerDetails = ({ educator }) =>
  educator?.metaInfo?.home_feed_blocks?.dpp_summary_banner ?? emptyObject;

export const getDppScheduleDetails = ({ educator }) =>
  educator?.dppReminderStatus;

export const getDPPQuestions = ({ educator }) =>
  educator.dpp.questions ?? emptyList;

export const getDPPFilterParams = ({ educator }) =>
  educator.dpp.data?.filters ?? emptyObject;

export const getDppSelectedFilters = ({ educator }) =>
  educator.dpp.data?.selectedFilters ?? emptyObject;

export const getDPPQuestionLoadingState = ({ educator }) =>
  educator.dpp.isLoading;

export const getDPPQuestionOrder = ({ educator }) => educator.dpp.questionOrder;

export const getHasUnsavedChanges = ({ educator }) =>
  educator.dpp.hasUnsavedChanges;

export const getDPPQuizInfo = ({ educator }) => {
  const quizInfo = educator.dpp.data?.class_details;
  if (quizInfo) {
    return {
      name: quizInfo.name,
      startTime: educator.dpp.data?.class_details?.live_at,
      uid: educator.dpp.data?.uid,
      rank: quizInfo.rank
    };
  }
  return emptyObject;
};

export const getDPPQuestionShufflingState = ({ educator }) =>
  educator.dpp.isShuffling;

export const getFeedbackOptions = ({ educator }) =>
  educator.dpp.feedback_options;

export const getDPPQuizState = ({ educator }) => educator.dpp.data?.state;

export const getCommunityOnboarding = ({ educator }) =>
  educator.communityOnboarding;

export const getDPPCoursePageLink = ({ educator }) =>
  educator.dpp.coursePageLink;

export const getConceptList = ({ educator }) =>
  educator.dpp.data.filters?.conceptList ?? emptyList;

export const getSubConceptMap = ({ educator }) =>
  educator.dpp.data.filters?.subConceptMap ?? emptyObject;

export const getIsDataNormalized = ({ educator }) =>
  educator.dpp.data.filters?.isDataNormalized ?? false;

export const getSelectedDifficultyCount = ({ educator }) =>
  educator.dpp.data.filters?.selectedDifficultyCount ?? emptyObject;

export const getIsSharedToCommunity = ({ educator }) =>
  educator.dpp.isSharedToCommunity;

export const getIsFeedbackSubmitted = ({ educator }) =>
  educator.dpp.isFeedbackSubmitted;

export const getDPPGoalName = ({ educator }) => educator.dpp.data.goal_name;

export const getDPPGoalUID = ({ educator }) => educator.dpp.data.goal_uid;

export const getIsLoading = ({ educator }) => educator.newSession.isLoading;

export const getRoomsOfAnOfflineCenter =
  (centreUID) =>
  ({ educator }) =>
    educator.rooms[centreUID] ?? emptyList;
